<script setup>
import { ref, computed, onMounted } from 'vue'
import { Dialog } from 'quasar'
import { useRoute } from 'vue-router'
import dayjs from 'dayjs'

import { getI18n } from '@/plugins/i18n'
import { useDetectDevice } from '@/composables/device'
import { usePlanningStore } from '@/stores/planning.store'
import { ABSENCE_ICON, useScheduleStore } from '@/stores/schedule.store'
import { useWebSiteStore } from '@/stores/web-site.store'
import { usePortalStore } from '@/stores/portal.store'
import { useNotify } from '@/composables/notify'
import { calendarPresets, clampToPresets, rangeForPreset } from '@/composables/date-range-presets'
import { sortByFields } from '@/composables/filter-sort'
import { useStaffMovements } from '@/composables/staff-movements'

import DateRangeNavigator from '@/components/shared/DateRangeNavigator.vue'
import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import BottomTabsView from '@/components/shared/BottomTabsView.vue'
import StaffMovementListItem from '@/components/planner/StaffMovementListItem.vue'
import VacationEditor from '@/components/shared/VacationEditor.vue'
import StaffMovementsEdit from '@/components/shared/StaffMovementsEdit.vue'
import NoStockConfirmDialog from '@/components/shared/NoStockConfirmDialog.vue'

const i18n = getI18n()
const { isMobile } = useDetectDevice()
const route = useRoute()
const planningStore = usePlanningStore()
const scheduleStore = useScheduleStore()
const webSiteStore = useWebSiteStore()
const portalStore = usePortalStore()
const { notifySuccess } = useNotify()
const { groupAllocations, noStockDialog, approveAllocationAsync, revertAllocationAsync } = useStaffMovements()

const activeTab = ref('holidays')
const tabs = [
  { name: 'holidays', label: i18n.t('common.staff.holidays') },
  { name: 'projects', label: i18n.t('common.staff.projectMovements') },
]
const holidays = ref([])
const allocations = ref([])
const editVacation = ref(null)
const holidaysEditMode = ref(false)
const addAllocation = ref(null)

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-plus',
    onClick: () => { addAllocation.value = { vacation: activeTab.value === 'holidays' } },
  },
]


const onAllocationAdded = async () => {
  addAllocation.value = null
  await refreshAsync()
}

const filterSort = computed(() => webSiteStore.filterSortFields[route.name])

const defaultPreset = clampToPresets(
  webSiteStore.getDefaultTimelinePeriod(),
  calendarPresets,
  portalStore.dateRangePresetEnum.MONTH,
)
const initialRange = rangeForPreset(defaultPreset)
const range = ref({ from: initialRange.from.format('YYYY-MM-DD'), to: initialRange.to.format('YYYY-MM-DD') })

const toRows = (res) => res?.data?.results ?? res?.data ?? []
const rangeParams = () => ({
  start: dayjs(range.value.from).startOf('day').toISOString(),
  end: dayjs(range.value.to).endOf('day').toISOString(),
})

const fetchHolidaysAsync = async () => {
  holidays.value = toRows(await scheduleStore.fetchEventsAsync({ vacation: true, all: true, ...rangeParams() }))
}

const fetchAllocationsAsync = async () => {
  allocations.value = toRows(await scheduleStore.fetchEventsAsync({
    allocations: true,
    service_type: portalStore.serviceTypeEnum.STAFF,
    ...rangeParams(),
  }))
}

const refreshAsync = () => (activeTab.value === 'holidays' ? fetchHolidaysAsync() : fetchAllocationsAsync())

onMounted(async () => {
  await Promise.all([fetchHolidaysAsync(), fetchAllocationsAsync()])
})

const onRangeChange = async (value) => {
  range.value = value
  await refreshAsync()
}

const onTabChange = async (value) => {
  activeTab.value = value
  await refreshAsync()
}

const staffName = (serviceId) => planningStore.getServiceName(planningStore.getServiceById(serviceId))

const matchesSearch = (...texts) => {
  const query = filterSort.value?.filterValue?.trim().toLowerCase()
  if (!query) return true
  return texts.some(text => (text ?? '').toLowerCase().includes(query))
}

const sortValue = (row, field) => {
  if (field === 'start') return row.start ?? null
  return (row.name ?? staffName(row.service_id))?.toLowerCase() ?? null
}

const filteredHolidays = computed(() => sortByFields(
  holidays.value.filter(event => matchesSearch(staffName(event.service_id))),
  filterSort.value?.sortFields,
  sortValue,
))

const allocationRows = computed(() => groupAllocations(allocations.value))

const filteredAllocationRows = computed(() => sortByFields(
  allocationRows.value.filter(row => matchesSearch(row.name, row.staffName)),
  filterSort.value?.sortFields,
  sortValue,
))

const onVacationUpdate = async () => {
  editVacation.value = null
  await fetchHolidaysAsync()
}

const onApprove = async (event) => {
  await scheduleStore.upsertEventAsync({ id: event.id, confirmed: true })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchHolidaysAsync()
}

const onRevert = async (event) => {
  await scheduleStore.upsertEventAsync({ id: event.id, confirmed: false })
  notifySuccess(i18n.t('info.updatedSuccessfully'))
  await fetchHolidaysAsync()
}

const onApproveAllocation = (group) => approveAllocationAsync(group, fetchAllocationsAsync)

const onRevertAllocation = (group) => revertAllocationAsync(group, fetchAllocationsAsync)

const onDelete = (event) => {
  Dialog.create({ message: i18n.t('common.confirmDelete'), cancel: true, persistent: true }).onOk(async () => {
    await scheduleStore.deleteEventAsync(event.id)
    notifySuccess(i18n.t('info.deletedSuccessfully'))
    await fetchHolidaysAsync()
  })
}
</script>
<template>
  <bottom-tabs-view
    :model-value="activeTab"
    :tabs="tabs"
    @update:model-value="onTabChange"
  >
    <no-stock-confirm-dialog v-model="noStockDialog" />
    <q-dialog
      :maximized="isMobile"
      :model-value="editVacation !== null"
      class="dialog-xs"
      @hide="editVacation = null"
    >
      <vacation-editor
        v-if="editVacation !== null"
        :model-value="editVacation"
        @update="onVacationUpdate"
        @cancel="editVacation = null"
      />
    </q-dialog>
    <q-dialog
      maximized
      :model-value="addAllocation !== null"
      @hide="addAllocation = null"
    >
      <staff-movements-edit
        v-if="addAllocation !== null"
        :vacation="addAllocation.vacation ?? false"
        @update="onAllocationAdded"
        @cancel="addAllocation = null"
      />
    </q-dialog>

    <filter-sort-selector hide-archived />

    <div class="q-px-sm q-py-xs">
      <date-range-navigator
        :model-value="range"
        :default-preset="defaultPreset"
        :allowed-presets="calendarPresets"
        compact
        @update:model-value="onRangeChange"
      />
    </div>
    <q-separator />

    <q-tab-panels
      :model-value="activeTab"
      animated
      class="col scroll"
      style="min-height: 0;"
    >
      <q-tab-panel name="holidays" class="q-pa-none">
        <div v-if="filteredHolidays.length" class="row justify-end q-px-md q-pt-sm">
          <q-btn
            flat
            round
            dense
            :icon="holidaysEditMode ? 'mdi-pencil-off' : 'mdi-pencil'"
            :color="holidaysEditMode ? 'primary' : 'grey-6'"
            @click="holidaysEditMode = !holidaysEditMode"
          >
            <q-tooltip>{{ holidaysEditMode ? i18n.t('common.done') : i18n.t('common.edit') }}</q-tooltip>
          </q-btn>
        </div>
        <q-list v-if="filteredHolidays.length">
          <staff-movement-list-item
            v-for="event in filteredHolidays"
            :key="event.id"
            :event="event"
            :label="staffName(event.service_id)"
            :avatar-icon="ABSENCE_ICON"
            show-status
            :can-approve="webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS)"
            :edit-mode="holidaysEditMode"
            @approve="onApprove"
            @revert="onRevert"
            @edit="editVacation = { ...$event }"
            @delete="onDelete"
          />
        </q-list>
        <div v-else class="text-center text-grey-6 q-pa-xl">
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>

      <q-tab-panel name="projects" class="q-pa-none">
        <q-list v-if="filteredAllocationRows.length">
          <staff-movement-list-item
            v-for="group in filteredAllocationRows"
            :key="group.key"
            :event="group"
            :label="group.name"
            :subtitle="group.staffName"
            :color="group.color"
            avatar-icon="mdi-briefcase-outline"
            show-status
            :can-approve="webSiteStore.can(webSiteStore.permissionFlagEnum.APPROVALS)"
            @approve="onApproveAllocation"
            @revert="onRevertAllocation"
          />
        </q-list>
        <div v-else class="text-center text-grey-6 q-pa-xl">
          {{ i18n.t('common.noData') }}
        </div>
      </q-tab-panel>
    </q-tab-panels>
  </bottom-tabs-view>
</template>
